// SessionAccordion.jsx
import React, { useMemo, useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { ChevronDown, Layers, ExternalLink, Copy, Check } from "lucide-react"
import TourCard from "../TourCard"
import { groupTours } from "../../utils/parsers/tourse"
import { formatLocalizedDate, money } from "../../utils/parsers/common"
import { useTranslation } from "react-i18next"

const buildSessionUrl = (sessionKey, locale, isShare = false) => {
  if (!isShare) {
    return `${window.location.origin}/book/saved_tours/session/${sessionKey}`
  }
  return `${window.location.origin}/imported-tours/${sessionKey}`
}

// Human-friendly stand-in for the raw session_key (a UUID nobody wants to
// read or search by). session_number is the min tour id in the session —
// sequential and unique — formatted as "SES-000123".
const formatSessionNumber = (n) => (n != null ? `SES-${String(n).padStart(6, "0")}` : "—")

const SessionAccordion = ({ session, sort, showPhotos, defaultOpen = false }) => {
  const { t, i18n } = useTranslation()

  const [open, setOpen] = useState(defaultOpen)
  const [copied, setCopied] = useState(false)

  const sorted = useMemo(() => {
    const grouped = groupTours(session.tours)
    const stars = (g) => parseInt(g.star_name, 10) || 0
    const arr = [...grouped]
    if (sort === "price_asc") arr.sort((a, b) => a.minPrice - b.minPrice)
    if (sort === "price_desc") arr.sort((a, b) => b.minPrice - a.minPrice)
    if (sort === "stars_desc") arr.sort((a, b) => stars(b) - stars(a))
    return arr
  }, [session.tours, sort])

  const minPrice = sorted.length ? Math.min(...sorted.map((t) => t.minPrice)) : null
  const currency = sorted[0]?.currency
  const sessionUrl = buildSessionUrl(session.session_key, i18n.language)

  const shareSessionUrl = buildSessionUrl(session.session_key, i18n.language, true)

  const handleCopyLink = async (e) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(shareSessionUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {}
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E7EAEF] overflow-hidden">
      <div className="w-full flex items-center justify-between gap-4 px-5 py-4 hover:bg-[#F8F9FB] transition-colors">
        <button
          onClick={() => setOpen((v) => !v)}
          className="flex items-center gap-3 min-w-0 flex-1 text-left"
        >
          <ChevronDown
            size={16}
            className={`shrink-0 text-[#9AA5B4] transition-transform ${open ? "rotate-180" : ""}`}
          />
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-[#26303D]">
                {formatLocalizedDate(session.created_at, i18n.language)}
              </span>
              {session.temporary ? (
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#FFF4E5] text-[#E8871E]">
                  {t("saved_tours.draft")}
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full bg-[#E9F7EF] text-[#1E9E5B]">
                  {t("saved_tours.confirmed")}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 text-xs text-[#9AA5B4] mt-0.5">
              <span className="inline-flex items-center gap-1">
                <Layers size={12} /> {t("saved_tours.tours_count", { count: session.tours_count })}
              </span>
              <span className="font-mono truncate max-w-[160px]" title={session.session_key}>
                {formatSessionNumber(session.session_number)}
              </span>
            </div>
          </div>
        </button>

        <div className="flex items-center gap-4 shrink-0">
          <div className="flex items-center gap-1.5 w-[72px] justify-end shrink-0">
            {!session.temporary && (
              <>
                <button
                  onClick={handleCopyLink}
                  title={t("saved_tours.copy_link")}
                  className="p-2 rounded-lg text-[#9AA5B4] hover:text-[#26303D] hover:bg-[#EEF1F5] transition-colors"
                >
                  {copied ? <Check size={17} className="text-[#1E9E5B]" /> : <Copy size={17} />}
                </button>

                <a
                  href={sessionUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  title={t("saved_tours.open_session")}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#5A6779] hover:text-[#26303D] hover:bg-[#F4F6F9] transition-colors"
                >
                  <ExternalLink size={15} />
                </a>
              </>
            )}
          </div>

          {minPrice != null && (
            <div className="text-right w-[110px] shrink-0">
              <div className="text-[11px] text-[#9AA5B4]">{t("saved_tours.from")}</div>
              <div className="text-sm font-mono font-semibold text-[#26303D]">
                {money(minPrice, currency)}
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-4 p-4 pt-0">
              {sorted.map((tour, i) => (
                <TourCard
                  key={tour.id ?? tour.price_id}
                  tour={tour}
                  index={i}
                  showPhotos={showPhotos}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default SessionAccordion
